import React from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { SectionTitle } from '@repo/components/containers';
import styles from './DuplicateSidebar.module.scss';
import { Overlay, Tooltip } from '@repo/components/tooltips';
import { ActionIgnoreIcon, ClayProfileIcon, LoaderIcon } from '@repo/icons';
import { Avatar } from '@repo/components/avatars';
import { SourcesList } from '@components/Contact/Sources';
import Action from '@components/Button/Action';
import { ID } from '@repo/utils/types';
import Button from '@components/Button';
import { useDispatch, useSelector } from 'react-redux';
import { selectDuplicate } from '@repo/redux/reducers/data/duplicates';
import { ignoreDuplicate, ignoreDuplicateMemberProfile } from '@repo/redux/actions/duplicates';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useTheme } from '@repo/themes';
import { mergeDuplicateMemberProfiles } from '@views/CommandBar/actionsBySection/duplicateActions';

const ContactCard = ({ contact, onIgnore }) => {
  return (
    <>
      <div className={styles.cardHeader}>
        <Avatar
          className={styles.avatar}
          url={contact.avatarURL}
          size="md"
          name={contact.displayName}
          initials={contact.displayName[0]}
          bgGlow
        />
        <div className={styles.nameAndActions}>
          <span className={styles.name}>{contact.displayName}</span>
          <div className={styles.cardActions}>
            <Tooltip placement="bottom" overlay={<Overlay>View Profile</Overlay>}>
              <Link to={`/contact/${contact.id}`} className={styles.profileLink}>
                <ClayProfileIcon color="textPrimary" width="16" height="16" viewBox="0 0 18 18" />
              </Link>
            </Tooltip>
            <Tooltip placement="bottom" overlay={<Overlay>Ignore Profile</Overlay>}>
              <Button onClick={onIgnore}>
                <ActionIgnoreIcon color="textPrimary" width="16" height="16" hoverOpacity />
              </Button>
            </Tooltip>
          </div>
        </div>
        {contact.primaryLocation?.formatted && (
          <span className={cn('text-small', styles.location)}>
            {contact.primaryLocation.formatted}
          </span>
        )}
      </div>
      <hr className={styles.divider} />
      <div className={styles.cardSources}>
        <span className="text-small">Sources</span>
        <div className={styles.sourcesList}>
          <SourcesList contactSources={contact.information} noActions />
        </div>
      </div>
    </>
  );
};

export default function DuplicateSidebar({ duplicateID }: { duplicateID: ID }) {
  const dispatch = useDispatch();
  const [theme] = useTheme();
  const duplicate = useSelector(selectDuplicate(duplicateID));
  const btnsDisabled = duplicate?.isMerging || duplicate?.isTransitioningOut;

  return (
    <div
      className={cn(
        styles.sidebarContent,
        btnsDisabled && styles.disabled,
        duplicate?.isTransitioningOut && styles.transitioningOut,
      )}
    >
      <SectionTitle underline>{duplicate?.contacts.length} Potential Duplicates</SectionTitle>
      <p className={styles.sidebarDescription}>
        These might be duplicates, but differences prevent auto-merge.
      </p>
      <div className={styles.contactCards}>
        <TransitionGroup>
          {duplicate?.contacts.map((contact) => (
            <CSSTransition
              key={contact.id}
              timeout={150}
              classNames={{
                exit: styles.cardExit,
                exitActive: styles.cardExitActive,
              }}
            >
              <div className={cn(styles.card, theme === 'light' && styles.light)}>
                <ContactCard
                  contact={contact}
                  onIgnore={() => dispatch(ignoreDuplicateMemberProfile(duplicateID, contact.id))}
                />
              </div>
            </CSSTransition>
          ))}
        </TransitionGroup>
      </div>
      <div className={styles.sidebarActions}>
        <Action
          onClick={() => dispatch(ignoreDuplicate(duplicateID))}
          secondary
          className={cn(styles.sidebarBtn)}
          disabled={btnsDisabled}
        >
          Ignore
        </Action>
        <Action
          onClick={() => mergeDuplicateMemberProfiles(duplicate, dispatch)}
          className={cn(styles.sidebarBtn)}
          disabled={btnsDisabled}
        >
          {duplicate.isMerging ? (
            <>
              Merging <LoaderIcon width="11" />
            </>
          ) : (
            'Merge'
          )}
        </Action>
      </div>
    </div>
  );
}
